import { ProfileMenu } from '@components/layout/side-nav/bars/top-bar';
import { PrimaryNav } from '@components/layout/side-nav/bars/top-bar/primary-nav';
import { topBarStyles } from '@components/layout/side-nav/bars/top-bar/topBarStyle';
import { BrandLogo, Button, Text, ThemeToggle } from '@components/ui';
import { AdminChatSocketEvents } from '@config/socketEvents';
import { SendEmail } from '@features/auth';
import { ChatDialogs } from '@features/chat';
import { useLogoutMutation } from '@lib/api/auth';
import { useDashboardQuery } from '@lib/api/dashboard';
import { useProfileQuery } from '@lib/api/profile';
import useLogout from '@lib/auth/useLogout';
import {
  getAdminChatJoinPayload,
  getAdminChatRoomId,
} from '@lib/chat/adminChatRoom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import type { SxProps, Theme } from '@mui/material';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Drawer,
  IconButton,
  Toolbar,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Socket } from 'socket.io-client';

import type {
  TopBarDashboardResponse,
  TopBarProfileResponse,
} from './topBarTypes';
const NAV_ICON_SX: SxProps<Theme> = {
  fontSize: 22,
  color: 'currentColor',
};

const UNREAD_BADGE_SX: SxProps<Theme> = {
  '& .MuiBadge-badge': {
    minWidth: 20,
    height: 20,
    px: '5px',
    borderRadius: 999,
    border: 2,
    borderColor: 'background.default',
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    boxSizing: 'border-box',
  },
};

const shellSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 1120,
  mx: 'auto',
  px: { xs: 2, md: 4 },
  boxSizing: 'border-box',
};

const rootPaths = new Set(['/', '/work-profile', '/settings', '/hire-a-r8tr']);

const pageTitleKey: Record<string, string> = {
  '/hire-a-r8tr': 'nav.hireR8tr',
  '/personal-info': 'nav.personalInformation',
  '/payment-methods': 'nav.paymentMethods',
  '/service-area': 'nav.serviceAreas',
  '/area-expertise': 'nav.areasOfExpertise',
  '/level-expertise': 'nav.rateExpertise',
  '/work-profile': 'nav.workProfile',
  '/notification': 'nav.notifications',
  '/support': 'nav.support',
  '/settings': 'nav.settings',
  '/job-detail': 'jobDetail.fallbackTitle',
  '/detail': 'jobDetail.fallbackTitle',
  '/area-of-expertise': 'nav.areasOfExpertise',
  '/rating-detail': 'pageTitle.ratingDetail',
  '/r8ting': 'pageTitle.r8ting',
  '/r8tr-rating': 'pageTitle.r8trRating',
  '/buyer-rating-r8tr': 'pageTitle.r8trRating',
  '/upload-videos': 'pageTitle.uploadVideos',
  '/uploaded-videos': 'pageTitle.uploadedVideos',
  '/special-notes': 'pageTitle.specialNotes',
  '/notes': 'pageTitle.notes',
  '/submit-payment': 'pageTitle.submitPayment',
  '/tip-payment': 'pageTitle.tipPayment',
  '/pdf': 'pageTitle.report',
  '/ebay-hire-a-r8tr': 'pageTitle.updateHire',
};

const titleFromPath = (pathname: string): string => {
  const slug = pathname.split('/').find(Boolean)?.replace(/-/g, ' ') ?? '';
  return slug.charAt(0).toUpperCase() + slug.slice(1);
};

interface TopBarProps {
  socket?: Socket;
  ebayItem?: boolean;
}

const TopBar = ({ socket, ebayItem = false }: TopBarProps): JSX.Element => {
  const { t } = useTranslation();
  const storedItemId = ebayItem;
  const navigator = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const userRole = localStorage.getItem('userRole');
  const { data: dashboardBody = {} } = useDashboardQuery();
  const dashboardData = dashboardBody as TopBarDashboardResponse;
  const stats = dashboardData.data;
  const [userProfilePic, setUserProfilePic] = useState<string | undefined>();
  const [messageCounter, setMessageCounter] = useState<number | null>(null);
  const [showChatBox, setShowChatBox] = useState<boolean>(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const storedUser = localStorage.getItem('user');
  const parentUserId = storedUser
    ? (JSON.parse(storedUser) as { user?: { parentUserId?: string | null } })
        .user?.parentUserId
    : null;
  const classes = topBarStyles;
  const { data: profileBody = {} } = useProfileQuery();
  const userDetails = profileBody as TopBarProfileResponse;
  useEffect(() => {
    setUserProfilePic(userDetails?.data?.user?.avatar);
  }, [userDetails]);

  const logoutRequest = useLogoutMutation();

  const handleToken = (): void => {
    logoutRequest.mutate(
      {
        deviceType: 'web',
        userId: userDetails?.data?.user?.id,
      },
      { onSettled: () => logout() }
    );
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>): void =>
    setProfileAnchor(event.currentTarget);
  const handleProfileMenuClose = (): void => setProfileAnchor(null);
  const handleSignOut = (): void => {
    handleProfileMenuClose();
    handleToken();
  };

  useEffect(() => {
    setMessageCounter(stats?.unSeenMessagesCount ?? null);
  }, [dashboardData]);
  useEffect(() => {
    if (location.pathname == '/hire-a-r8tr') {
      return;
    } else {
      socket!.on(AdminChatSocketEvents.UNSEEN_COUNT, (data: unknown) => {
        if (!showChatBox) {
          setMessageCounter(data as number | null);
        }
      });
    }
  }, [socket]);
  const handleChatDialogClose = (): void => {
    const roomId = getAdminChatRoomId(userDetails?.data?.user?.id);
    if (roomId) {
      socket!.emit(AdminChatSocketEvents.LEAVE, roomId);
    }
    setShowChatBox(false);
  };
  const handleChatDialog = (): void => {
    const data = getAdminChatJoinPayload(userDetails?.data?.user?.id);
    if (data) {
      socket!.emit(AdminChatSocketEvents.JOIN, data);
    }
    setMessageCounter(null);
    setShowChatBox(true);
  };

  const user = userDetails?.data?.user;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const hasUnseenMessages = messageCounter != null && messageCounter > 0;
  const showBack =
    !rootPaths.has(location.pathname) && !/^\/\d+$/.test(location.pathname);
  const titleKey = pageTitleKey[location.pathname];
  const title =
    location.pathname === '/' || /^\/\d+$/.test(location.pathname)
      ? t('shell.roleDashboard', {
          role: userRole === 'r8tr' ? 'R8TR' : 'Buyer',
        })
      : titleKey
        ? t(titleKey)
        : titleFromPath(location.pathname);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        {!storedItemId ? (
          <>
            <Toolbar sx={[classes.topBar, shellSx]} disableGutters>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <IconButton
                  aria-label={title}
                  onClick={() => setNavOpen(true)}
                  sx={{ display: { md: 'none' }, color: 'text.secondary' }}
                >
                  <MenuIcon />
                </IconButton>
                <BrandLogo height={32} onClick={() => navigator('/')} />
                <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0 }}>
                  <PrimaryNav role={userRole} />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {userRole === 'buyer' && !parentUserId ? (
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => setInviteOpen(true)}
                    sx={{ display: { xs: 'none', sm: 'inline-flex' }, mr: 0.5 }}
                  >
                    {t('nav.invite')}
                  </Button>
                ) : null}
                <ThemeToggle iconOnly />
                <IconButton
                  aria-label={t('common.messages')}
                  onClick={() => handleChatDialog()}
                  sx={{ color: 'text.secondary' }}
                >
                  <Badge
                    badgeContent={hasUnseenMessages ? messageCounter : 0}
                    max={99}
                    sx={UNREAD_BADGE_SX}
                  >
                    <ChatBubbleOutlineRoundedIcon sx={NAV_ICON_SX} />
                  </Badge>
                </IconButton>
                <Avatar
                  alt={fullName || 'Profile'}
                  src={userProfilePic}
                  onClick={handleProfileMenuOpen}
                  sx={{ width: 36, height: 36, cursor: 'pointer', ml: 0.5 }}
                />
              </Box>
            </Toolbar>
            <Box
              sx={{
                ...shellSx,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 52,
              }}
            >
              {showBack ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigator(-1)}
                  startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                >
                  {t('common.back')}
                </Button>
              ) : null}
              <Text
                as="h1"
                variant="title"
                sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}
              >
                {title}
              </Text>
            </Box>
          </>
        ) : (
          <Toolbar sx={shellSx} disableGutters>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <BrandLogo height={32} />
              <Text variant="title">{t('shell.getVehicleRated')}</Text>
              <Button variant="outlined" onClick={handleToken}>
                <PowerSettingsNewIcon sx={{ mr: 1 }} />
                {t('common.signOut')}
              </Button>
            </Box>
          </Toolbar>
        )}
      </AppBar>
      <Drawer
        anchor="left"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        PaperProps={{ sx: { width: 280, p: 2, bgcolor: 'background.default' } }}
      >
        <Text variant="subtitle" sx={{ mb: 2 }}>
          {title}
        </Text>
        <PrimaryNav
          role={userRole}
          direction="column"
          onNavigate={() => setNavOpen(false)}
        />
        {userRole === 'buyer' && !parentUserId ? (
          <Button
            variant="outlined"
            onClick={() => {
              setNavOpen(false);
              setInviteOpen(true);
            }}
            sx={{ mt: 2 }}
          >
            {t('nav.invite')}
          </Button>
        ) : null}
      </Drawer>
      {showChatBox && (
        <ChatDialogs
          handleChatDialogClose={handleChatDialogClose}
          userData={userDetails?.data?.user}
          showChatBox={showChatBox}
          socket={socket}
        />
      )}
      {inviteOpen ? (
        <SendEmail
          open={inviteOpen}
          handleCloseSendEmailModal={() => setInviteOpen(false)}
        />
      ) : null}
      <ProfileMenu
        anchorEl={profileAnchor}
        onClose={handleProfileMenuClose}
        fullName={fullName}
        email={user?.email}
        phoneNumber={user?.phoneNumber}
        userRole={userRole}
        onNavigate={(path) => navigator(path)}
        onSignOut={handleSignOut}
      />
    </>
  );
};

export default TopBar;
